import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { sentenceCase } from 'change-case';
import { Form, Formik } from 'formik';
import React, { FC } from 'react';
import QuillEditor from 'src/components/QuillEditor';
import Truncate from 'src/components/Truncate';
import DurationPicker from 'src/features/store/components/DurationPicker';
import LookupConfigSection from 'src/features/store/components/LookupConfigSection';
import type { StoreFormProps, StoreFormValues } from 'src/features/store/types/store.types';
import { enabled } from 'src/features/store/utils/lookupConfig';
import { Theme } from 'src/theme';
import {
  DataDTOTimezoneInfo,
  DataEnumerationsDiscountCodeProvider,
  DataEnumerationsLoyaltyProgramProvider,
  DataEnumerationsPosSystem,
} from 'src/types/api/generated/api';
import { removeHTML } from 'src/utils/common';
import { formatDuration } from 'src/utils/duration';
import { phone } from 'src/utils/regex';
import * as Yup from 'yup';

const MIN_TIMEOUT_SECONDS = 60;
const MAX_TIMEOUT_SECONDS = 2 ** 31 - 1;

const optionalPositiveNumber = (modeField: keyof StoreFormValues) =>
  Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .when(modeField, { is: enabled, then: Yup.number().min(1) });

const activeToSchema = (
  modeField: keyof StoreFormValues,
  activeFromField: keyof StoreFormValues,
) =>
  Yup.string().test(
    'after-active-from',
    'Active to must be after active from',
    function isAfterActiveFrom(value) {
      if (!enabled(this.parent[modeField])) return true;
      const activeFrom = this.parent[activeFromField];
      return !value || !activeFrom || value > activeFrom;
    },
  );

const validationSchema = (
  hasLoyaltyProgramUsername: boolean,
  hasLoyaltyProgramPassword: boolean,
) =>
  Yup.object().shape({
    name: Yup.string().max(255).required(),
    code: Yup.string().max(255),
    address1: Yup.string().max(255),
    address2: Yup.string().max(255),
    city: Yup.string().max(50),
    state: Yup.string().max(50),
    zip: Yup.string().max(6),
    phone: Yup.string().matches(phone.regex, phone.message).max(20),
    email: Yup.string().email().max(255),
    contactName: Yup.string().max(255),
    orderIdleTimeoutInMinutes: Yup.number().min(1).max(999),
    purchasedItemTimeoutPeriodInSeconds: Yup.number()
      .min(MIN_TIMEOUT_SECONDS, `Timeout must be at least ${formatDuration(MIN_TIMEOUT_SECONDS)}`)
      .max(MAX_TIMEOUT_SECONDS, 'Timeout is too large.'),
    startListeningOnlyViaTrigger: Yup.boolean(),
    notes: Yup.string().test(
      'editorTextLength',
      'Must be at most 5000 characters',
      (v) => removeHTML(v ?? '').length <= 5000,
    ),
    timezone: Yup.string().required().typeError('Timezone is a required field'),
    data1: Yup.string().max(255),
    loyaltyProgramMode: Yup.string().required(),
    loyaltyProgramProvider: Yup.string().when('loyaltyProgramMode', {
      is: enabled,
      then: Yup.string().required().oneOf(Object.values(DataEnumerationsLoyaltyProgramProvider)),
    }),
    loyaltyProgramPosSystem: Yup.string()
      .notOneOf([DataEnumerationsPosSystem.Treez])
      .when(
        ['loyaltyProgramMode', 'loyaltyProgramProvider'],
        (mode: string, provider: string, schema: Yup.StringSchema) => {
          if (!enabled(mode)) return schema;
          if (provider === DataEnumerationsLoyaltyProgramProvider.YinzCam) {
            return schema
              .required()
              .oneOf([DataEnumerationsPosSystem.Square, DataEnumerationsPosSystem.Stripe]);
          }
          if (provider === DataEnumerationsLoyaltyProgramProvider.Shopify) {
            return schema.required().oneOf([DataEnumerationsPosSystem.Stripe]);
          }
          return schema.required().notOneOf([DataEnumerationsPosSystem.None]);
        },
      ),
    loyaltyProgramMaximumAttempts: optionalPositiveNumber('loyaltyProgramMode'),
    loyaltyProgramActiveFrom: Yup.string(),
    loyaltyProgramActiveTo: activeToSchema('loyaltyProgramMode', 'loyaltyProgramActiveFrom'),
    loyaltyProgramButtonLabel: Yup.string().max(200),
    loyaltyProgramButtonColor: Yup.string().max(50),
    loyaltyProgramSuccessMessage: Yup.string().max(1000),
    loyaltyProgramFailureMessage: Yup.string().max(1000),
    loyaltyProgramHostname: Yup.string()
      .max(2048)
      .when(['loyaltyProgramMode', 'loyaltyProgramProvider'], {
        is: (mode: string, provider: string) =>
          enabled(mode) && provider === DataEnumerationsLoyaltyProgramProvider.YinzCam,
        then: Yup.string().required(),
      }),
    loyaltyProgramUsername: Yup.string()
      .max(1000)
      .test(
        'username-clear-conflict',
        'Cannot enter a username while it is set to be cleared',
        function hasNoClearConflict(value) {
          if (!enabled(this.parent.loyaltyProgramMode)) return true;
          return !(value && this.parent.clearLoyaltyProgramUsername);
        },
      )
      .test(
        'username-required',
        'Username is required for this loyalty provider',
        function hasUsername(value) {
          const values = this.parent as StoreFormValues;
          if (
            !enabled(values.loyaltyProgramMode) ||
            values.loyaltyProgramProvider !== DataEnumerationsLoyaltyProgramProvider.YinzCam
          ) {
            return true;
          }
          if (values.clearLoyaltyProgramUsername) {
            return this.createError({
              message: 'Cannot clear the username while this loyalty provider is enabled',
            });
          }
          return Boolean(value || hasLoyaltyProgramUsername);
        },
      ),
    loyaltyProgramPassword: Yup.string()
      .max(1000)
      .test(
        'password-clear-conflict',
        'Cannot enter a password while it is set to be cleared',
        function hasNoClearConflict(value) {
          if (!enabled(this.parent.loyaltyProgramMode)) return true;
          return !(value && this.parent.clearLoyaltyProgramPassword);
        },
      )
      .test(
        'password-required',
        'Password is required for this loyalty provider',
        function hasPassword(value) {
          const values = this.parent as StoreFormValues;
          if (
            !enabled(values.loyaltyProgramMode) ||
            values.loyaltyProgramProvider !== DataEnumerationsLoyaltyProgramProvider.YinzCam
          ) {
            return true;
          }
          if (values.clearLoyaltyProgramPassword) {
            return this.createError({
              message: 'Cannot clear the password while this loyalty provider is enabled',
            });
          }
          return Boolean(value || hasLoyaltyProgramPassword);
        },
      ),
    clearLoyaltyProgramUsername: Yup.boolean(),
    clearLoyaltyProgramPassword: Yup.boolean(),
    discountCodeMode: Yup.string().required(),
    discountCodeProvider: Yup.string().when('discountCodeMode', {
      is: enabled,
      then: Yup.string().required().oneOf(Object.values(DataEnumerationsDiscountCodeProvider)),
    }),
    discountCodePosSystem: Yup.string().when(
      ['discountCodeMode', 'discountCodeProvider'],
      (mode: string, provider: string, schema: Yup.StringSchema) => {
        if (!enabled(mode)) return schema;
        if (provider === DataEnumerationsDiscountCodeProvider.Local) {
          return schema.required().oneOf([DataEnumerationsPosSystem.Square]);
        }
        return schema.required().notOneOf([DataEnumerationsPosSystem.None]);
      },
    ),
    discountCodeMaximumAttempts: optionalPositiveNumber('discountCodeMode'),
    discountCodeActiveFrom: Yup.string(),
    discountCodeActiveTo: activeToSchema('discountCodeMode', 'discountCodeActiveFrom'),
    discountCodeButtonLabel: Yup.string().max(200),
    discountCodeButtonColor: Yup.string().max(50),
    discountCodeSuccessMessage: Yup.string().max(1000),
    discountCodeFailureMessage: Yup.string().max(1000),
  });

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  editor: {
    '& .ql-editor': {
      height: 400,
    },
  },
  btnRoot: {
    display: 'flex',
    alignItems: 'center',
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const StoreForm: FC<StoreFormProps> = ({
  mode,
  initialValues,
  timezones,
  submitLabel,
  isSubmitting: isSubmittingProp,
  hasLoyaltyProgramUsername = false,
  hasLoyaltyProgramPassword = false,
  onSubmit,
}) => {
  const classes = useStyles();
  const isEdit = mode === 'edit';

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema(
        hasLoyaltyProgramUsername,
        hasLoyaltyProgramPassword,
      )}
      onSubmit={async (values, { setStatus, setSubmitting }) => {
        await onSubmit(values);
        setStatus({ success: true });
        setSubmitting(false);
      }}
    >
      {(formik) => {
        const {
          errors,
          handleBlur,
          handleChange,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          touched,
          values,
        } = formik;

        return (
          <Form className={classes.root}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.name && errors.name)}
                          fullWidth
                          helperText={touched.name && errors.name && sentenceCase(errors.name)}
                          label="Store Name *"
                          name="name"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.name}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.code && errors.code)}
                          fullWidth
                          helperText={touched.code && errors.code && sentenceCase(errors.code)}
                          label="Code"
                          name="code"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.code}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.address1 && errors.address1)}
                          fullWidth
                          helperText={
                            touched.address1 && errors.address1 && sentenceCase(errors.address1)
                          }
                          label="Address Line 1"
                          name="address1"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.address1}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.address2 && errors.address2)}
                          fullWidth
                          helperText={
                            touched.address2 && errors.address2 && sentenceCase(errors.address2)
                          }
                          label="Address Line 2"
                          name="address2"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.address2}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.city && errors.city)}
                          fullWidth
                          helperText={touched.city && errors.city && sentenceCase(errors.city)}
                          label="City"
                          name="city"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.city}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.state && errors.state)}
                          fullWidth
                          helperText={touched.state && errors.state && sentenceCase(errors.state)}
                          label="State"
                          name="state"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.state}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.zip && errors.zip)}
                          fullWidth
                          helperText={touched.zip && errors.zip && sentenceCase(errors.zip)}
                          label="Zip"
                          name="zip"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.zip}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.contactName && errors.contactName)}
                          fullWidth
                          helperText={
                            touched.contactName &&
                            errors.contactName &&
                            sentenceCase(errors.contactName)
                          }
                          label="Contact Name"
                          name="contactName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.contactName}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.phone && errors.phone)}
                          fullWidth
                          helperText={touched.phone && errors.phone && sentenceCase(errors.phone)}
                          label="Contact Phone"
                          name="phone"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.phone}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.email && errors.email)}
                          fullWidth
                          helperText={touched.email && errors.email && sentenceCase(errors.email)}
                          label="Contact Email"
                          name="email"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.email}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        {isEdit && (
                          <Box mb={1}>
                            <Typography variant="caption" display="block">
                              Please leave the Hidden Menu Passcode field empty if you do not want
                              to change it.
                            </Typography>
                          </Box>
                        )}
                        <TextField
                          error={Boolean(touched.pin && errors.pin)}
                          fullWidth
                          helperText={touched.pin && errors.pin && sentenceCase(errors.pin)}
                          label="Hidden Menu Passcode"
                          name="pin"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.pin}
                          variant="outlined"
                          inputProps={{
                            form: {
                              autocomplete: 'off',
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(
                            touched.orderIdleTimeoutInMinutes && errors.orderIdleTimeoutInMinutes,
                          )}
                          fullWidth
                          helperText={
                            touched.orderIdleTimeoutInMinutes &&
                            errors.orderIdleTimeoutInMinutes &&
                            sentenceCase(errors.orderIdleTimeoutInMinutes)
                          }
                          label="Order Idle Timeout In Minutes"
                          name="orderIdleTimeoutInMinutes"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.orderIdleTimeoutInMinutes}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <DurationPicker
                          id="purchasedItemTimeoutPeriodInSeconds"
                          label="Purchased Item Timeout"
                          value={Number(values.purchasedItemTimeoutPeriodInSeconds) || 0}
                          onChange={(totalSeconds) =>
                            setFieldValue(
                              'purchasedItemTimeoutPeriodInSeconds',
                              totalSeconds.toString(),
                            )
                          }
                          onBlur={() => setFieldTouched('purchasedItemTimeoutPeriodInSeconds', true)}
                          error={Boolean(
                            touched.purchasedItemTimeoutPeriodInSeconds &&
                              errors.purchasedItemTimeoutPeriodInSeconds,
                          )}
                          helperText={
                            (touched.purchasedItemTimeoutPeriodInSeconds &&
                              errors.purchasedItemTimeoutPeriodInSeconds &&
                              sentenceCase(errors.purchasedItemTimeoutPeriodInSeconds)) ||
                            'How long a purchased tag is ignored if it is read again at this store.'
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <FormControl fullWidth variant="outlined" error={Boolean(touched.timezone && errors.timezone)}>
                          <Autocomplete
                            id="timezone"
                            value={
                              timezones.find((t) => t.id === values.timezone) ??
                              (values.timezone
                                ? { id: values.timezone, name: values.timezone }
                                : null)
                            }
                            options={timezones}
                            getOptionLabel={(option: DataDTOTimezoneInfo) => option.name}
                            getOptionSelected={(option, selected) => option.id === selected.id}
                            onChange={(_e, value: DataDTOTimezoneInfo | null) => {
                              setFieldValue('timezone', value?.id || '');
                            }}
                            autoHighlight
                            onBlur={handleBlur}
                            renderOption={({ name }) => <Truncate value={name} length={50} />}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={Boolean(touched.timezone && errors.timezone)}
                                fullWidth
                                label="Timezone *"
                                name="timezone"
                                variant="outlined"
                              />
                            )}
                          />
                          <FormHelperText>
                            {touched.timezone && errors.timezone && sentenceCase(errors.timezone)}
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <TextField
                          error={Boolean(touched.data1 && errors.data1)}
                          fullWidth
                          helperText={touched.data1 && errors.data1 && sentenceCase(errors.data1)}
                          label="Data Field 1"
                          name="data1"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.data1}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <Box mt={1} mb={1}>
                        <FormControl
                          component="fieldset"
                          error={Boolean(
                            touched.startListeningOnlyViaTrigger &&
                              errors.startListeningOnlyViaTrigger,
                          )}
                        >
                          <FormLabel component="legend">Start Listening Only Via Trigger</FormLabel>
                          <RadioGroup
                            row
                            aria-label="startListeningOnlyViaTrigger"
                            name="startListeningOnlyViaTrigger"
                            value={values.startListeningOnlyViaTrigger}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                            <FormControlLabel value="false" control={<Radio />} label="No" />
                          </RadioGroup>
                          <FormHelperText>
                            {touched.startListeningOnlyViaTrigger &&
                              errors.startListeningOnlyViaTrigger &&
                              sentenceCase(String(errors.startListeningOnlyViaTrigger))}
                          </FormHelperText>
                        </FormControl>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Box mt={1} mb={1}>
                        <Typography variant="subtitle1" color="textSecondary">
                          Notes
                        </Typography>
                      </Box>
                      <Paper variant="outlined">
                        <QuillEditor
                          className={classes.editor}
                          value={values.notes}
                          onChange={(value: string) => setFieldValue('notes', value)}
                        />
                      </Paper>
                      {touched.notes && errors.notes && (
                        <Box mt={2}>
                          <FormHelperText error>{sentenceCase(errors.notes)}</FormHelperText>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <LookupConfigSection
              kind="loyalty"
              hasLoyaltyProgramUsername={hasLoyaltyProgramUsername}
              hasLoyaltyProgramPassword={hasLoyaltyProgramPassword}
            />
            <LookupConfigSection kind="discount" />
          </Grid>
          <Box mt={2} className={classes.btnRoot}>
            <div className={classes.wrapper}>
              <Button
                color="primary"
                variant="contained"
                disabled={isSubmittingProp || isSubmitting}
                type="submit"
              >
                {submitLabel}
              </Button>
              {(isSubmittingProp || isSubmitting) && (
                <CircularProgress size={24} className={classes.buttonProgress} />
              )}
            </div>
          </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StoreForm;
